<script setup>
import { onMounted, onBeforeUnmount, watch, ref } from "vue";
import { useRouter } from "vue-router";
import { useSurveySave } from "@/composables/useSurveySave";

const props = defineProps({
  currentStage: Number,
  totalStages: Number,
  surveyData: {
    type: Object,
    required: true,
  },
  currentUser: {
    type: Object,
    default: null,
  },
});

const router = useRouter();
const { saveStatus, saveSurveyData } = useSurveySave();
const testMode = ref(false);

// Prevent navigation back from Thank You stage
const preventNavigation = (event) => {
  // Prevent browser back button
  if (event.type === "popstate") {
    event.preventDefault();
    window.history.pushState(null, "", window.location.href);
    return false;
  }

  // Prevent programmatic navigation back
  if (event.type === "beforeunload") {
    return false;
  }
};

// Override router navigation to prevent going back
const preventRouterNavigation = () => {
  // Disable browser back button
  window.history.pushState(null, "", window.location.href);

  // Override router.go(-1) and similar navigation
  if (router && router.go) {
    const originalGo = router.go;
    router.go = (delta) => {
      if (delta < 0) {
        console.log("ThankYouStage: Preventing back navigation");
        return false;
      }
      return originalGo.call(router, delta);
    };
  }
};

// Clear localStorage when save is successful
const clearLocalStorage = () => {
  try {
    // Check if localStorage is available
    if (typeof window !== "undefined" && window.localStorage) {
      // Set survey completion flag before clearing
      localStorage.setItem("surveyCompleted", "true");

      // Clear survey progress
      localStorage.removeItem("surveyProgress");

      // Clear any other survey-related localStorage items
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes("survey") ||
            key.includes("competency") ||
            key.includes("progress"))
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));

      console.log("ThankYouStage: localStorage cleared successfully");
    }
  } catch (error) {
    console.error("ThankYouStage: Error clearing localStorage:", error);
  }
};

onMounted(async () => {
  // Check if in test mode
  if (typeof window !== "undefined") {
    testMode.value = !!window.__SURVEY_TEST_MODE__;
  }

  console.log(
    "ThankYouStage: Component mounted, currentUser:",
    props.currentUser ? "available" : "not available"
  );

  // Add navigation prevention only if we're in a browser environment AND not in test mode
  if (typeof window !== "undefined" && !testMode.value) {
    window.addEventListener("popstate", preventNavigation);
    window.addEventListener("beforeunload", preventNavigation);

    // Push current state to prevent back navigation
    window.history.pushState(null, "", window.location.href);

    // Override router navigation
    preventRouterNavigation();
  }

  // Since we now ensure authentication before reaching this stage,
  // we can directly attempt to save the data
  if (testMode.value) {
    // In test mode, just show success without trying to save
    saveStatus.value = {
      loading: false,
      error: null,
      success: true,
    };
  } else if (props.currentUser) {
    console.log("ThankYouStage: User available, saving survey data...");
    await saveSurveyData(props.surveyData, props.currentUser);

    // Clear localStorage if save was successful
    if (saveStatus.value.success) {
      clearLocalStorage();
    }
  } else {
    console.error(
      "ThankYouStage: No user available - this should not happen with route guards in place"
    );
    saveStatus.value = {
      loading: false,
      error: "Authentication error. Please refresh the page and try again.",
      success: false,
    };
  }
});

onBeforeUnmount(() => {
  // Remove event listeners only if we're in a browser environment
  if (typeof window !== "undefined") {
    window.removeEventListener("popstate", preventNavigation);
    window.removeEventListener("beforeunload", preventNavigation);
  }
});

// Watch for successful save and clear localStorage
watch(
  () => saveStatus.value.success,
  (newSuccess) => {
    if (newSuccess) {
      clearLocalStorage();
    }
  }
);
</script>

<template>
  <div class="thank-you-container">
    <img
      src="@/assets/cyberireland_logo.png"
      alt="Cyber Ireland Logo"
      class="logo"
      style="width: 300px; margin: 0 auto; display: block"
    />
    <h2>Thank You for Completing the Survey!</h2>
    <div class="message">
      <template v-if="saveStatus.loading">
        <p>Saving your responses...</p>
      </template>
      <template v-else-if="saveStatus.error">
        <p class="error-message">
          {{ saveStatus.error }}
        </p>
        <p>Please try again or contact support if the problem persists.</p>
      </template>
      <template v-else>
        <p>We appreciate your time and feedback.</p>
        <p>Your responses have been successfully recorded.</p>
      </template>
    </div>
  </div>
</template>

<style scoped>
.thank-you-container {
  text-align: center;
  padding: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

.thank-you-container h2 {
  color: var(--primary-color);
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  line-height: 1.3;
}

.message {
  margin-top: 2rem;
  line-height: 1.6;
  font-size: 1rem;
}

.message p {
  margin-bottom: 1rem;
}

.message p:last-child {
  margin-bottom: 0;
}

.error-message {
  color: #dc3545;
  background-color: #f8d7da;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  border: 1px solid #f5c6cb;
  font-size: 0.95rem;
  line-height: 1.4;
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .thank-you-container {
    padding: 1.5rem 1rem;
  }

  .thank-you-container h2 {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }

  .message {
    margin-top: 1.5rem;
    font-size: 0.95rem;
  }

  .error-message {
    padding: 0.875rem;
    font-size: 0.9rem;
    margin: 0.875rem 0;
  }
}

@media (max-width: 480px) {
  .thank-you-container {
    padding: 1rem 0.75rem;
  }

  .thank-you-container h2 {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .message {
    margin-top: 1.25rem;
    font-size: 0.9rem;
  }

  .error-message {
    padding: 0.75rem;
    font-size: 0.85rem;
    margin: 0.75rem 0;
  }
}
</style>
